import React, { useEffect, useState } from "react";
import adminApi from "../../services/adminApi";
import toast from "react-hot-toast";
import PricingForm from "./PricingForm";
import LoadingState from "../../components/Admin/LoadingState";
import "../../styles/Admin.css";

export default function PricingSettings() {
  const [cfg, setCfg] = useState(null);
  const [spacesPricing, setSpacesPricing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingSpaces, setSavingSpaces] = useState(false);
  const [loadingSpaces, setLoadingSpaces] = useState(true);

  useEffect(() => {
    adminApi
      .get("/pricing/admin/settings")
      .then((res) => setCfg(res.data))
      .catch(() => toast.error("Failed to load pricing settings"));

    // Load Spaces pricing
    adminApi
      .get("/admin/settings/pricing/spaces")
      .then((res) => setSpacesPricing(res.data))
      .catch(() => toast.error("Failed to load Spaces pricing"))
      .finally(() => setLoadingSpaces(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put("/pricing/admin/settings", cfg);
      toast.success("Settings saved successfully");
    } catch (_) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const saveSpacesPricing = async () => {
    setSavingSpaces(true);
    try {
      await adminApi.put("/admin/settings/pricing/spaces", spacesPricing);
      toast.success("Spaces pricing saved successfully");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to save Spaces pricing"
      );
    } finally {
      setSavingSpaces(false);
    }
  };

  const addStorageTier = () => {
    setSpacesPricing({
      ...spacesPricing,
      storage_tiers: [
        ...(spacesPricing.storage_tiers || []),
        { size_gb: 0, price: 0 },
      ],
    });
  };

  const updateStorageTier = (index, field, value) => {
    const tiers = [...(spacesPricing.storage_tiers || [])];
    tiers[index] = { ...tiers[index], [field]: Number(value) };
    setSpacesPricing({ ...spacesPricing, storage_tiers: tiers });
  };

  const removeStorageTier = (index) => {
    const tiers = spacesPricing.storage_tiers.filter((_, i) => i !== index);
    setSpacesPricing({ ...spacesPricing, storage_tiers: tiers });
  };

  if (!cfg) {
    return <LoadingState message="Loading pricing settings..." />;
  }

  return (
    <>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Pricing Settings</h1>
        <p className="admin-page-subtitle">
          Configure pricing margins and currency conversion rates
        </p>
      </div>

      {/* General Pricing Settings */}
      <div className="admin-section" style={{ marginBottom: 32 }}>
        <div className="admin-section-header">
          <h3 className="admin-section-title">General Pricing</h3>
          <p className="admin-section-subtitle">
            Configure margins and currency conversion
          </p>
        </div>
        <PricingForm config={cfg} onChange={setCfg} saving={saving} />
        <div className="admin-form-actions" style={{ marginTop: 24 }}>
          <button className="btn-primary" onClick={save} disabled={saving}>
            {saving ? "Saving..." : "Save General Settings"}
          </button>
        </div>
      </div>

      {/* Spaces Pricing Settings */}
      {loadingSpaces ? (
        <LoadingState message="Loading Spaces pricing..." />
      ) : spacesPricing ? (
        <div className="admin-section">
          <div className="admin-section-header">
            <h3 className="admin-section-title">Spaces Pricing</h3>
            <p className="admin-section-subtitle">
              Configure storage tier pricing for DigitalOcean Spaces
            </p>
          </div>

          <div className="admin-form">
            <div className="form-group">
              <label className="form-label">
                Base Price (
                {spacesPricing.currency === "INR"
                  ? "USD (converted to INR)"
                  : "USD"}
                )
              </label>
              <input
                type="number"
                step="0.01"
                className="form-control"
                value={spacesPricing.base_price || 5.0}
                onChange={(e) =>
                  setSpacesPricing({
                    ...spacesPricing,
                    base_price: Number(e.target.value),
                  })
                }
              />
              <small className="form-text text-muted">
                {spacesPricing.currency === "INR" && cfg?.usdToInr ? (
                  <>
                    Default price for Spaces if no tier matches. Display price:
                    ${(spacesPricing.base_price || 5.0).toFixed(2)} USD = ₹
                    {((spacesPricing.base_price || 5.0) * cfg.usdToInr).toFixed(
                      2
                    )}{" "}
                    INR
                  </>
                ) : (
                  "Default price for Spaces if no tier matches"
                )}
              </small>
            </div>

            <div className="form-group">
              <label className="form-label">Currency</label>
              <select
                className="form-control"
                value={spacesPricing.currency || "USD"}
                onChange={(e) =>
                  setSpacesPricing({
                    ...spacesPricing,
                    currency: e.target.value,
                  })
                }
              >
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="GBP">GBP (£)</option>
                <option value="INR">INR (₹)</option>
              </select>
              {spacesPricing.currency === "INR" && cfg?.usdToInr && (
                <small
                  className="form-text text-muted"
                  style={{ marginTop: 4 }}
                >
                  Conversion rate: 1 USD = ₹{cfg.usdToInr.toFixed(2)} (from
                  General Pricing)
                </small>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Margin (%)</label>
              <input
                type="number"
                step="0.01"
                className="form-control"
                value={spacesPricing.margin_pct ?? 0}
                onChange={(e) =>
                  setSpacesPricing({
                    ...spacesPricing,
                    margin_pct: Number(e.target.value),
                  })
                }
              />
              <small className="form-text text-muted">
                Percentage markup applied to base price and storage tiers (e.g.,
                20 = 20% markup)
              </small>
            </div>

            <div className="form-group">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 12,
                }}
              >
                <label className="form-label">Storage Tiers</label>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={addStorageTier}
                  style={{ padding: "6px 12px", fontSize: 13 }}
                >
                  + Add Tier
                </button>
              </div>

              {spacesPricing.storage_tiers &&
              spacesPricing.storage_tiers.length > 0 ? (
                <div style={{ display: "grid", gap: 12 }}>
                  {spacesPricing.storage_tiers.map((tier, index) => (
                    <div
                      key={index}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr auto",
                        gap: 12,
                        alignItems: "end",
                        padding: 12,
                        background: "#f8f9fa",
                        borderRadius: 6,
                        border: "1px solid #e9ecef",
                      }}
                    >
                      <div>
                        <label
                          className="form-label"
                          style={{ fontSize: 12, marginBottom: 4 }}
                        >
                          Size (GB)
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={tier.size_gb || ""}
                          onChange={(e) =>
                            updateStorageTier(index, "size_gb", e.target.value)
                          }
                          placeholder="e.g. 250"
                        />
                      </div>
                      <div>
                        <label
                          className="form-label"
                          style={{ fontSize: 12, marginBottom: 4 }}
                        >
                          Price (USD/month)
                          {spacesPricing.currency === "INR" &&
                            cfg?.usdToInr && (
                              <span
                                style={{
                                  color: "#6c757d",
                                  fontWeight: "normal",
                                }}
                              >
                                {" "}
                                → ₹
                                {((tier.price || 0) * cfg.usdToInr).toFixed(2)}
                              </span>
                            )}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          value={tier.price || ""}
                          onChange={(e) =>
                            updateStorageTier(index, "price", e.target.value)
                          }
                          placeholder="e.g. 5.00"
                        />
                      </div>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => removeStorageTier(index)}
                        style={{
                          padding: "8px 12px",
                          fontSize: 13,
                          background: "#fee2e2",
                          color: "#dc2626",
                          borderColor: "#fecaca",
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    padding: 24,
                    textAlign: "center",
                    background: "#f8f9fa",
                    borderRadius: 6,
                    border: "1px dashed #dee2e6",
                    color: "#6c757d",
                  }}
                >
                  No storage tiers configured. Click "Add Tier" to create one.
                </div>
              )}
            </div>
          </div>

          <div className="admin-form-actions" style={{ marginTop: 24 }}>
            <button
              className="btn-primary"
              onClick={saveSpacesPricing}
              disabled={savingSpaces}
            >
              {savingSpaces ? "Saving..." : "Save Spaces Pricing"}
            </button>
          </div>
        </div>
      ) : (
        <div className="text-muted">Failed to load Spaces pricing</div>
      )}
    </>
  );
}
